/*
 * Layout do painel: barra lateral com os modulos e cabecalho com o usuario.
 * Os itens das etapas futuras aparecem desabilitados, para o mapa do sistema
 * ficar visivel desde o inicio.
 */
import { useState, type ComponentType, type SVGProps } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { PERMISSIONS, type Permission } from '@hotspot/shared';
import { useAuth } from '../lib/auth';
import { Logo } from './Logo';
import { Button } from './ui';
import {
  IconBuilding,
  IconChart,
  IconClose,
  IconDashboard,
  IconDocument,
  IconHistory,
  IconLogout,
  IconMegaphone,
  IconMenu,
  IconPlug,
  IconRouter,
  IconShield,
  IconSignal,
  IconUsers,
} from './icons';

interface NavItem {
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  to?: string;
  permission?: Permission;
  /** Etapa em que o item passa a existir. */
  stage?: number;
}

const NAV: NavItem[] = [
  { label: 'Dashboard', to: '/', icon: IconDashboard },
  { label: 'MikroTiks', to: '/mikrotiks', permission: PERMISSIONS.MIKROTIK_READ, icon: IconRouter },
  { label: 'Unidades', to: '/unidades', permission: PERMISSIONS.UNIT_READ, icon: IconBuilding },
  {
    label: 'Campanhas',
    to: '/campanhas',
    permission: PERMISSIONS.CAMPAIGN_READ,
    icon: IconMegaphone,
  },
  { label: 'Clientes', to: '/clientes', permission: PERMISSIONS.CUSTOMER_READ, icon: IconUsers },
  /* Ao lado de Clientes porque e sobre consentimento de cliente, nao sobre
     roteador. A aparencia e o modo de acesso ficam no painel do equipamento,
     junto do botao que envia para o MikroTik — que e onde o trabalho acontece. */
  { label: 'Termos', to: '/termos', permission: PERMISSIONS.CUSTOMER_READ, icon: IconDocument },
  { label: 'Sessoes', to: '/sessoes', permission: PERMISSIONS.SESSION_READ, icon: IconSignal },
  { label: 'Relatorios', to: '/relatorios', permission: PERMISSIONS.REPORT_READ, icon: IconChart },
  {
    label: 'Integracoes',
    to: '/integracoes',
    permission: PERMISSIONS.INTEGRATION_READ,
    icon: IconPlug,
  },
  {
    label: 'Administradores',
    to: '/administradores',
    permission: PERMISSIONS.ADMIN_READ,
    icon: IconShield,
  },
  { label: 'Auditoria', to: '/auditoria', permission: PERMISSIONS.AUDIT_READ, icon: IconHistory },
];

/** Iniciais para o avatar. Duas bastam; tres viram borrao no circulo. */
function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '?';
  const primeira = partes[0]![0] ?? '';
  const ultima = partes.length > 1 ? (partes[partes.length - 1]![0] ?? '') : '';
  return (primeira + ultima).toUpperCase();
}

export function AppShell() {
  const { session, logout, can } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  async function handleLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  const atual = NAV.find((item) => item.to === pathname) ?? NAV[0]!;

  const menu = (
    <>
      <div className="flex items-center justify-between px-5 py-5">
        <span className="flex items-center gap-2.5 text-base font-bold text-white">
          <Logo className="size-9" />
          Hotspot
        </span>

        <button
          type="button"
          onClick={() => setMenuOpen(false)}
          aria-label="Fechar menu"
          className="rounded-lg p-1.5 text-white/60 hover:bg-white/10 hover:text-white lg:hidden"
        >
          <IconClose className="size-5" />
        </button>
      </div>

      <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 pb-6">
        {NAV.filter((item) => !item.permission || can(item.permission)).map((item) => {
          const Icone = item.icon;

          if (!item.to) {
            return (
              <span
                key={item.label}
                title={`Disponivel na Etapa ${item.stage}`}
                className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-white/30"
              >
                <Icone className="size-5 shrink-0" />
                <span className="flex-1">{item.label}</span>
                {item.stage ? (
                  <span className="rounded bg-white/10 px-1.5 py-0.5 text-[10px]">
                    E{item.stage}
                  </span>
                ) : null}
              </span>
            );
          }

          return (
            <NavLink
              key={item.label}
              to={item.to}
              onClick={() => setMenuOpen(false)}
              className={({ isActive }) =>
                `relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${
                  isActive
                    ? 'bg-white/12 font-semibold text-white'
                    : 'font-medium text-white/65 hover:bg-white/8 hover:text-white'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {/* Marca do item ativo: some junto com o item, sem deixar sobra. */}
                  {isActive ? (
                    <span className="absolute top-1/2 -left-3 h-6 w-1 -translate-y-1/2 rounded-r-full bg-brand-300" />
                  ) : null}
                  <Icone className="size-5 shrink-0" />
                  {item.label}
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="border-t border-white/10 px-5 py-4 text-[11px] text-white/35">
        Hotspot Manager
      </div>
    </>
  );

  return (
    <div className="flex min-h-full">
      {/* Fundo escurecido do menu no celular: fechar tocando fora e esperado. */}
      {menuOpen ? (
        <button
          type="button"
          aria-label="Fechar menu"
          onClick={() => setMenuOpen(false)}
          className="fixed inset-0 z-30 bg-slate-950/50 backdrop-blur-xs lg:hidden"
        />
      ) : null}

      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-64 flex-col bg-brand-950 print:hidden lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 ${
          menuOpen ? 'translate-x-0' : '-translate-x-full'
        } transition-transform duration-200 lg:transition-none`}
      >
        {menu}
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex items-center gap-3 print:hidden border-b border-slate-200/70 bg-white/85 px-4 py-3 backdrop-blur-md lg:px-8">
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label="Abrir menu"
            className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 lg:hidden"
          >
            <IconMenu className="size-5" />
          </button>

          {/* Onde estou: no celular o menu esta fechado e nada mais diz isso. */}
          <span className="truncate text-sm font-semibold text-slate-800">{atual.label}</span>

          <div className="ml-auto flex items-center gap-3">
            <div className="hidden text-right sm:block">
              <p className="text-sm font-semibold text-slate-800">{session?.admin.name}</p>
              <p className="text-xs text-slate-500">{session?.admin.roleLabel}</p>
            </div>

            <span
              title={session?.admin.email}
              className="grid size-9 shrink-0 place-items-center rounded-full bg-linear-to-br from-brand-400 to-brand-600 text-xs font-bold text-white"
            >
              {iniciais(session?.admin.name ?? '')}
            </span>

            <Button variant="ghost" size="sm" onClick={() => void handleLogout()}>
              <IconLogout className="size-4" />
              <span className="hidden sm:inline">Sair</span>
            </Button>
          </div>
        </header>

        <main className="mx-auto w-full max-w-7xl flex-1 p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
